import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Wand2, Play, Download, CheckCircle2, Loader2, RefreshCw, AlertTriangle, History, Trash2, StopCircle, Database, FlaskConical, Rocket, Save, Filter, Layers, Undo2, RotateCcw } from 'lucide-react';

const NUMERIC_TYPES = ['int', 'bigint', 'smallint', 'tinyint', 'decimal', 'numeric', 'float', 'real', 'money', 'smallmoney', 'bit'];

const STATUS_COLORS = {
  pending: 'bg-amber-500/15 text-amber-600',
  running: 'bg-blue-500/15 text-blue-600',
  completed: 'bg-emerald-500/15 text-emerald-600',
  failed: 'bg-red-500/15 text-red-600',
};

const ColSelect = ({ value, onChange, columns, placeholder, testId, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger data-testid={testId}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {columns.map((c) => (
        <SelectItem key={c.name} value={c.name}>
          {c.name} ({c.data_type}){c.is_primary_key ? ' — PK' : ''}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function DataMassaging() {
  const { api } = useAuth();
  const [tables, setTables] = useState([]);
  const [tablesError, setTablesError] = useState('');
  const [srcTable, setSrcTable] = useState('');
  const [srcCols, setSrcCols] = useState([]);
  const [srcColumn, setSrcColumn] = useState('');
  const [prompt, setPrompt] = useState('');
  const [rowLimit, setRowLimit] = useState(100);
  const [updateMode, setUpdateMode] = useState('same_table');
  const [updateColumn, setUpdateColumn] = useState('');
  const [targetTable, setTargetTable] = useState('');
  const [targetCols, setTargetCols] = useState([]);
  const [targetFk, setTargetFk] = useState('');
  const [filterColumn, setFilterColumn] = useState('');
  const [filterOp, setFilterOp] = useState('contains');
  const [filterValue, setFilterValue] = useState('');
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [promptName, setPromptName] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);

  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [starting, setStarting] = useState(false);
  const [wb, setWb] = useState(null);
  const [wbTest, setWbTest] = useState(null);
  const [wbBusy, setWbBusy] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refining, setRefining] = useState(false);
  const pollRef = useRef(null);
  const logsEndRef = useRef(null);

  const loadTables = useCallback(async () => {
    try {
      const res = await api().get('/matching/tables');
      setTables(res.data.tables || []);
      setTablesError('');
    } catch (e) {
      setTablesError(e.response?.data?.detail || 'Failed to load Azure tables');
    }
  }, [api]);

  const loadJobs = useCallback(async () => {
    try {
      const res = await api().get('/massaging/jobs');
      setJobs(res.data.jobs || []);
    } catch (e) { /* noop */ }
  }, [api]);

  const loadPrompts = useCallback(async () => {
    try {
      const res = await api().get('/massaging/prompts');
      setSavedPrompts(res.data.prompts || []);
    } catch (e) { /* noop */ }
  }, [api]);

  useEffect(() => { loadTables(); loadJobs(); loadPrompts(); }, [loadTables, loadJobs, loadPrompts]);

  useEffect(() => {
    if (!srcTable) { setSrcCols([]); return; }
    setFilterColumn(''); setFilterValue('');
    api().get(`/matching/tables/${srcTable}/columns`).then((r) => setSrcCols(r.data.columns || [])).catch(() => {});
  }, [srcTable, api]);

  useEffect(() => {
    if (!targetTable) { setTargetCols([]); return; }
    api().get(`/matching/tables/${targetTable}/columns`).then((r) => setTargetCols(r.data.columns || [])).catch(() => {});
  }, [targetTable, api]);

  const fetchJob = useCallback(async (id) => {
    try {
      const res = await api().get(`/massaging/jobs/${id}`);
      setJob(res.data);
      return res.data;
    } catch (e) { return null; }
  }, [api]);

  useEffect(() => {
    if (!job || !['pending', 'running'].includes(job.status)) {
      clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      const j = await fetchJob(job.id);
      if (j && !['pending', 'running'].includes(j.status)) {
        clearInterval(pollRef.current);
        loadJobs();
        toast[j.status === 'completed' ? 'success' : 'error'](
          j.status === 'completed' ? 'Massaging job finished' : `Job failed: ${j.error || 'unknown error'}`);
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [job?.id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [job?.logs?.length]);

  const canRun = srcTable && srcColumn && prompt.trim().length >= 3 && updateColumn &&
    (updateMode === 'same_table' || (targetTable && targetFk)) &&
    (!filterColumn || !['equals', 'contains'].includes(filterOp) || filterValue.trim());

  const savePrompt = async () => {
    if (!promptName.trim()) { toast.error('Enter a name for the prompt'); return; }
    if (prompt.trim().length < 3) { toast.error('Write a prompt first'); return; }
    setSavingPrompt(true);
    try {
      await api().post('/massaging/prompts', { name: promptName.trim(), prompt: prompt.trim() });
      toast.success(`Prompt "${promptName.trim()}" saved`);
      setPromptName('');
      loadPrompts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  const deletePrompt = async (pid) => {
    await api().delete(`/massaging/prompts/${pid}`);
    loadPrompts();
    toast.success('Prompt deleted');
  };

  const runJob = async () => {
    setStarting(true);
    setWb(null); setWbTest(null);
    try {
      const payload = {
        source_table: srcTable, source_column: srcColumn, ai_prompt: prompt.trim(),
        update_mode: updateMode, update_column: updateColumn, row_limit: Number(rowLimit) || 100,
      };
      if (updateMode === 'target_table') {
        payload.target_table = targetTable;
        payload.target_fk_column = targetFk;
      }
      const res = await api().post('/massaging/jobs', payload);
      toast.success('Massaging job started');
      await fetchJob(res.data.job_id);
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start job');
    } finally {
      setStarting(false);
    }
  };

  const cancelJob = async () => {
    await api().post(`/massaging/jobs/${job.id}/cancel`);
    toast.info('Cancellation requested');
  };

  const approveJob = async () => {
    try {
      await api().post(`/massaging/jobs/${job.id}/approve`);
      toast.success('Results approved — write-back unlocked');
      fetchJob(job.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const deleteJob = async (id) => {
    await api().delete(`/massaging/jobs/${id}`);
    if (job?.id === id) setJob(null);
    loadJobs();
    toast.success('Job deleted');
  };

  const exportCsv = async () => {
    try {
      const res = await api().get(`/massaging/jobs/${job.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `massaging_${job.id.slice(0, 8)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const applyRefine = async () => {
    setRefining(true);
    try {
      const res = await api().post(`/massaging/jobs/${job.id}/refine`, { ai_prompt: refinePrompt.trim() });
      toast.info(`Massaging layer ${res.data.layer} started`);
      setRefinePrompt('');
      setWb(null); setWbTest(null);
      fetchJob(job.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start refinement');
    } finally {
      setRefining(false);
    }
  };

  const undoLayer = async () => {
    try {
      const res = await api().post(`/massaging/jobs/${job.id}/undo-layer`);
      toast.success(`Layer ${res.data.undone} undone — previous results restored`);
      fetchJob(job.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
  };

  const retryFailed = async () => {
    setWbBusy(true);
    try {
      const res = await api().post(`/massaging/jobs/${job.id}/writeback-retry`);
      if (res.data.rows_failed > 0) {
        toast.warning(`Retry: ${res.data.updated} updated, ${res.data.rows_failed} still failing`);
      } else {
        toast.success(`Retry complete — all ${res.data.updated} row(s) updated`);
      }
      fetchJob(job.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    } finally {
      setWbBusy(false);
    }
  };

  const doWriteback = async (opts) => {
    setWbBusy(true);
    try {
      const res = await api().post(`/massaging/jobs/${job.id}/writeback`, opts);
      if (opts.test_one) setWbTest(res.data);
      else if (opts.dry_run) setWb(res.data);
      else {
        setWb(null);
        if (res.data.failed_rows?.length) {
          toast.warning(`Write-back: ${res.data.rows_updated} rows updated, ${res.data.rows_failed} failed (see error log)`);
        } else {
          toast.success(`Write-back complete — ${res.data.rows_updated} rows updated`);
        }
        fetchJob(job.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Write-back failed');
    } finally {
      setWbBusy(false);
    }
  };

  const isRunning = job && ['pending', 'running'].includes(job.status);
  const progress = job?.progress;
  const updateColSource = updateMode === 'same_table' ? srcCols : targetCols;
  const updateColType = updateColSource.find((c) => c.name === updateColumn)?.data_type;
  const numericTarget = updateColType && NUMERIC_TYPES.includes(updateColType.toLowerCase());

  return (
    <div className="space-y-6" data-testid="massaging-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope'] flex items-center gap-3">
            <Wand2 className="w-7 h-7 text-primary" /> Data Massaging
          </h1>
          <p className="text-muted-foreground mt-1">Transform Azure SQL column values with an AI prompt, review, approve, then write back.</p>
        </div>
        <Button variant="outline" onClick={() => { loadTables(); loadJobs(); }} data-testid="massaging-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {tablesError && (
        <Card className="border-red-300">
          <CardContent className="py-4 flex items-center gap-3 text-red-600" data-testid="massaging-tables-error">
            <AlertTriangle className="w-5 h-5" /> {tablesError}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Configuration</CardTitle>
            <CardDescription>Pick the source data, describe the transformation, and choose where results go.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source table</Label>
                <Select value={srcTable} onValueChange={(v) => { setSrcTable(v); setSrcColumn(''); setUpdateColumn(''); }}>
                  <SelectTrigger data-testid="massaging-source-table-select">
                    <SelectValue placeholder="Select table" />
                  </SelectTrigger>
                  <SelectContent>
                    {tables.map((t) => (
                      <SelectItem key={t.name} value={t.name}>{t.name} ({t.row_count} rows)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Source column (values to transform)</Label>
                <ColSelect value={srcColumn} onChange={setSrcColumn} columns={srcCols}
                  placeholder="Select column" testId="massaging-source-column-select" disabled={!srcTable} />
              </div>
            </div>

            <div className="p-4 rounded-lg bg-muted/50 border space-y-3" data-testid="massaging-filter-section">
              <p className="text-sm font-semibold flex items-center gap-2"><Filter className="w-4 h-4" /> Source row filter (optional)</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <Select value={filterColumn || '__none__'} onValueChange={(v) => setFilterColumn(v === '__none__' ? '' : v)} disabled={!srcTable}>
                  <SelectTrigger data-testid="massaging-filter-column-select">
                    <SelectValue placeholder="No filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No filter</SelectItem>
                    {srcCols.map((c) => (
                      <SelectItem key={c.name} value={c.name}>{c.name} ({c.data_type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterOp} onValueChange={setFilterOp} disabled={!filterColumn}>
                  <SelectTrigger data-testid="massaging-filter-op-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">contains</SelectItem>
                    <SelectItem value="equals">equals</SelectItem>
                    <SelectItem value="empty">is empty (NULL)</SelectItem>
                    <SelectItem value="not_empty">is not empty</SelectItem>
                  </SelectContent>
                </Select>
                {['equals', 'contains'].includes(filterOp) && (
                  <Input value={filterValue} onChange={(e) => setFilterValue(e.target.value)}
                    placeholder="Filter value" disabled={!filterColumn} data-testid="massaging-filter-value-input" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">Only rows matching this condition are loaded and transformed. Pick "is empty (NULL)" to target rows with no value.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>AI transformation prompt</Label>
                {savedPrompts.length > 0 && (
                  <Select value="" onValueChange={(pid) => {
                    const p = savedPrompts.find((sp) => sp.id === pid);
                    if (p) { setPrompt(p.prompt); toast.info(`Loaded prompt "${p.name}"`); }
                  }}>
                    <SelectTrigger className="w-56 h-8" data-testid="massaging-load-prompt-select">
                      <SelectValue placeholder="Load saved prompt..." />
                    </SelectTrigger>
                    <SelectContent>
                      {savedPrompts.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                placeholder='e.g. "Convert to Title Case and remove trailing punctuation" or "Translate to English"'
                data-testid="massaging-prompt-input" />
              <div className="flex flex-wrap items-center gap-2">
                <Input value={promptName} onChange={(e) => setPromptName(e.target.value)}
                  placeholder="Prompt name" className="w-48 h-8" data-testid="massaging-prompt-name-input" />
                <Button variant="outline" size="sm" onClick={savePrompt} disabled={savingPrompt} data-testid="massaging-save-prompt-btn">
                  {savingPrompt ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save prompt
                </Button>
                {savedPrompts.map((p) => (
                  <Badge key={p.id} variant="secondary" className="gap-1" data-testid={`massaging-prompt-badge-${p.id}`}>
                    {p.name}
                    <button onClick={() => deletePrompt(p.id)} className="hover:text-red-500" data-testid={`massaging-delete-prompt-${p.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rows to process (max 1000)</Label>
                <Input type="number" min={1} max={1000} value={rowLimit}
                  onChange={(e) => setRowLimit(e.target.value)} data-testid="massaging-row-limit-input" />
              </div>
              <div className="space-y-2">
                <Label>Write destination</Label>
                <Select value={updateMode} onValueChange={(v) => { setUpdateMode(v); setUpdateColumn(''); }}>
                  <SelectTrigger data-testid="massaging-update-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same_table">Same table (another column)</SelectItem>
                    <SelectItem value="target_table">Target table (matched via foreign key)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {updateMode === 'target_table' && (
              <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-lg bg-muted/50 border">
                <div className="space-y-2">
                  <Label>Target table</Label>
                  <Select value={targetTable} onValueChange={(v) => { setTargetTable(v); setTargetFk(''); setUpdateColumn(''); }}>
                    <SelectTrigger data-testid="massaging-target-table-select">
                      <SelectValue placeholder="Select target table" />
                    </SelectTrigger>
                    <SelectContent>
                      {tables.map((t) => (
                        <SelectItem key={t.name} value={t.name}>{t.name} ({t.row_count} rows)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Foreign-key column (matches source PK)</Label>
                  <ColSelect value={targetFk} onChange={setTargetFk} columns={targetCols}
                    placeholder="Select FK column" testId="massaging-target-fk-select" disabled={!targetTable} />
                  <p className="text-xs text-muted-foreground">Rows updated WHERE target.{targetFk || 'fk'} = source primary key</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Column to write transformed values into ({updateMode === 'same_table' ? 'source table' : 'target table'})</Label>
              <ColSelect value={updateColumn} onChange={setUpdateColumn} columns={updateColSource}
                placeholder="Select update column" testId="massaging-update-column-select"
                disabled={updateMode === 'same_table' ? !srcTable : !targetTable} />
              {numericTarget && (
                <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="massaging-numeric-warning">
                  <AlertTriangle className="w-3.5 h-3.5" /> [{updateColumn}] is a numeric column ({updateColType}) — AI output is text; write-back will be blocked unless every transformed value is a number.
                </p>
              )}
            </div>

            <Button onClick={runJob} disabled={!canRun || starting || isRunning} className="w-full" data-testid="massaging-run-btn">
              {starting || isRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {isRunning ? 'Job running...' : 'Run AI Transformation (preview — no writes)'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><History className="w-5 h-5" /> Job History</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="massaging-history-list">
            {jobs.length === 0 && <p className="text-sm text-muted-foreground">No massaging jobs yet.</p>}
            {jobs.map((j) => (
              <div key={j.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-muted/60 ${job?.id === j.id ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => { setWb(null); setWbTest(null); fetchJob(j.id); }}
                data-testid={`massaging-history-item-${j.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{j.config.source_table}.{j.config.source_column}</p>
                  <Badge className={STATUS_COLORS[j.status] || ''} variant="secondary">{j.status}</Badge>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleString()}</p>
                  <Button variant="ghost" size="icon" className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); deleteJob(j.id); }}
                    data-testid={`massaging-delete-job-${j.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {job && (
        <Card data-testid="massaging-job-detail">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="w-5 h-5" /> Job {job.id.slice(0, 8)}
                <Badge className={STATUS_COLORS[job.status] || ''} variant="secondary" data-testid="massaging-job-status">{job.status}</Badge>
                {job.approved && <Badge className="bg-emerald-500/15 text-emerald-600" variant="secondary">approved</Badge>}
                {job.writeback?.executed && <Badge className="bg-blue-500/15 text-blue-600" variant="secondary">written back</Badge>}
              </CardTitle>
              <CardDescription>
                {job.config.source_table}.{job.config.source_column} → {job.config.update_mode === 'same_table'
                  ? `${job.config.source_table}.${job.config.update_column}`
                  : `${job.config.target_table}.${job.config.update_column} (via ${job.config.target_fk_column})`}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {isRunning && (
                <Button variant="destructive" size="sm" onClick={cancelJob} data-testid="massaging-cancel-btn">
                  <StopCircle className="w-4 h-4 mr-1" /> Cancel
                </Button>
              )}
              {job.status === 'completed' && (
                <Button variant="outline" size="sm" onClick={exportCsv} data-testid="massaging-export-btn">
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {isRunning && progress && (
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Transforming...</span>
                  <span data-testid="massaging-progress-text">{progress.processed}/{progress.total}</span>
                </div>
                <Progress value={progress.total ? (progress.processed / progress.total) * 100 : 0} />
              </div>
            )}

            {job.stats && (
              <div className="grid grid-cols-3 gap-3">
                {[['Rows', job.stats.total], ['Transformed', job.stats.transformed], ['Changed', job.stats.changed]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border p-3 text-center">
                    <p className="text-2xl font-bold" data-testid={`massaging-stat-${l.toLowerCase()}`}>{v}</p>
                    <p className="text-xs text-muted-foreground">{l}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2" data-testid="massaging-layers-list">
              <Badge variant="secondary" className="bg-primary/10">Layer 1: {job.config.ai_prompt.slice(0, 60)}{job.config.ai_prompt.length > 60 ? '…' : ''}</Badge>
              {(job.layers || []).map((l) => (
                <Badge key={l.layer} variant="secondary" className="bg-purple-500/10 text-purple-700" data-testid={`massaging-layer-badge-${l.layer}`}>
                  Layer {l.layer}: {l.prompt.slice(0, 60)}{l.prompt.length > 60 ? '…' : ''}
                </Badge>
              ))}
              {job.status === 'completed' && job.undo_available && job.layers?.length > 0 && !job.writeback?.executed && (
                <Button variant="outline" size="sm" className="h-6 text-xs" onClick={undoLayer} data-testid="massaging-undo-layer-btn">
                  <Undo2 className="w-3 h-3 mr-1" /> Undo layer {job.layers[job.layers.length - 1].layer}
                </Button>
              )}
            </div>

            <div className="rounded-lg bg-zinc-950 text-zinc-100 p-3 font-mono text-xs max-h-48 overflow-y-auto" data-testid="massaging-logs-console">
              {(job.logs || []).map((l, i) => (
                <div key={i} className={l.level === 'error' ? 'text-red-400' : l.level === 'warning' ? 'text-amber-400' : ''}>
                  <span className="text-zinc-500">{l.ts?.slice(11, 19)}</span> {l.message}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>

            {job.results?.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium">PK</th>
                        <th className="text-left p-2 font-medium">Original</th>
                        <th className="text-left p-2 font-medium">Transformed</th>
                        <th className="text-left p-2 font-medium w-24">Changed</th>
                      </tr>
                    </thead>
                    <tbody data-testid="massaging-results-table">
                      {job.results.map((r) => (
                        <tr key={r.id} className="border-t">
                          <td className="p-2 text-muted-foreground">{r.source_pk}</td>
                          <td className="p-2 max-w-xs truncate" title={r.original}>{r.original}</td>
                          <td className="p-2 max-w-xs truncate" title={r.transformed || ''}>{r.transformed ?? <span className="text-red-500">failed</span>}</td>
                          <td className="p-2">
                            {r.changed
                              ? <Badge className="bg-emerald-500/15 text-emerald-600" variant="secondary">yes</Badge>
                              : <Badge variant="secondary">no</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {job.status === 'completed' && !job.writeback?.executed && (
              <div className="p-4 rounded-lg border space-y-3" data-testid="massaging-refine-panel">
                <p className="font-semibold text-sm flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Add another massaging layer (runs on the generated values)
                </p>
                <div className="flex items-start gap-2">
                  <Textarea value={refinePrompt} onChange={(e) => setRefinePrompt(e.target.value)} rows={2}
                    placeholder='e.g. "Now translate the result to English" or "Trim to 100 characters"'
                    data-testid="massaging-refine-prompt-input" className="flex-1" />
                  <Button onClick={applyRefine} disabled={refining || refinePrompt.trim().length < 3}
                    data-testid="massaging-refine-btn">
                    {refining ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Layers className="w-4 h-4 mr-1" />}
                    Apply layer {(job.layers?.length || 0) + 2}
                  </Button>
                </div>
                {job.approved && <p className="text-xs text-amber-600">Applying a new layer resets the approval — you'll need to approve again.</p>}
              </div>
            )}

            {job.status === 'completed' && !job.approved && (
              <Button onClick={approveJob} className="w-full" data-testid="massaging-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Approve Results (unlocks write-back)
              </Button>
            )}

            {job.approved && !job.writeback?.executed && (
              <div className="p-4 rounded-lg border space-y-3" data-testid="massaging-writeback-panel">
                <p className="font-semibold text-sm flex items-center gap-2"><Rocket className="w-4 h-4" /> Write back to Azure SQL</p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" disabled={wbBusy} onClick={() => doWriteback({ dry_run: true })} data-testid="massaging-wb-dryrun-btn">
                    Dry run
                  </Button>
                  <Button variant="outline" size="sm" disabled={wbBusy} onClick={() => doWriteback({ dry_run: true, test_one: true })} data-testid="massaging-wb-test-btn">
                    <FlaskConical className="w-4 h-4 mr-1" /> Test 1 record (rolled back)
                  </Button>
                  {wb && (
                    <Button variant="destructive" size="sm" disabled={wbBusy} onClick={() => doWriteback({ dry_run: false })} data-testid="massaging-wb-execute-btn">
                      Execute write-back ({wb.rows_to_update} rows)
                    </Button>
                  )}
                  {wbBusy && <Loader2 className="w-4 h-4 animate-spin mt-2" />}
                </div>
                {wb && (
                  <p className="text-sm text-muted-foreground" data-testid="massaging-wb-dryrun-result">
                    Dry run: {wb.rows_to_update} rows will update [{wb.table}].[{wb.column}] WHERE {wb.where_column} matches.
                  </p>
                )}
                {wbTest && (
                  <div className={`text-sm p-3 rounded-md ${wbTest.success ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700'}`}
                    data-testid="massaging-wb-test-result">
                    {wbTest.success ? wbTest.message : (wbTest.error || wbTest.message)}
                  </div>
                )}
              </div>
            )}

            {job.writeback?.executed && (
              <div className="space-y-2">
                <div className="p-4 rounded-lg bg-emerald-500/10 text-emerald-700 text-sm" data-testid="massaging-wb-done">
                  Write-back executed {job.writeback.executed_at ? `at ${new Date(job.writeback.executed_at).toLocaleString()}` : ''} — {job.writeback.rows_updated} rows updated in [{job.writeback.table}].[{job.writeback.column}] (batches of 20).
                </div>
                {job.writeback.failed_rows?.length > 0 && (
                  <div className="p-4 rounded-lg bg-red-500/10 text-red-700 text-sm space-y-2 max-h-56 overflow-y-auto" data-testid="massaging-wb-failures">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold">{job.writeback.rows_failed} row(s) failed and were skipped (see error log above):</p>
                      <Button size="sm" variant="outline" onClick={retryFailed} disabled={wbBusy} data-testid="massaging-retry-failed-btn">
                        {wbBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />} Retry failed rows
                      </Button>
                    </div>
                    {job.writeback.failed_rows.map((f, i) => (
                      <p key={i} className="text-xs">PK {f.pk}: {f.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
